// Sales: quotes and the pipeline, requests, proposals.
import { createRoute, Navigate } from "@tanstack/react-router";
import { Proposals } from "../../screens/Agency";
import { ProposalBuilder, ProposalSettingsPage, ProposalsPage } from "../../screens/Proposals";
import { QuoteForm } from "../../screens/quotes/Form";
import { QuotesPage } from "../../screens/quotes/List";
import { QuotePage } from "../../screens/quotes/Page";
import type { QuotesView } from "../../screens/quotes/common";
import { RequestPage, RequestsPage } from "../../screens/Requests";
import { SectionMessages } from "../../screens/settings/Automations";
import { FeatureSettings } from "../../screens/settings/Feature";
import { PriceSettings } from "../../screens/settings/Prices";
import { WebsiteSettings } from "../../screens/settings/Website";
import { useSession } from "../session";
import { Allowed, app, newSearch, page, sectionMessagePage } from "./base";

const quotesList = createRoute({
  getParentRoute: () => app,
  path: "/quotes",
  // Views List, Pipeline (2026-11-03) and Plan offers (2026-11-04). ?view= keeps the one picked; with none the page
  // opens on the last one.
  validateSearch: (s: Record<string, unknown>): { view?: QuotesView } =>
    s.view === "list" || s.view === "pipeline" || s.view === "offers" ? { view: s.view } : {},
  component: function QuotesRoute() {
    const { view } = quotesList.useSearch();
    return (
      <Allowed action="quote.read">
        <QuotesPage view={view} />
      </Allowed>
    );
  },
});

const quoteNew = createRoute({
  getParentRoute: () => app,
  path: "/quotes/new",
  validateSearch: (s: Record<string, unknown>): { leadId?: string; clientId?: string } => ({
    ...(typeof s.leadId === "string" && s.leadId ? { leadId: s.leadId } : {}),
    ...(typeof s.clientId === "string" && s.clientId ? { clientId: s.clientId } : {}),
  }),
  component: function QuoteNewRoute() {
    const { leadId, clientId } = quoteNew.useSearch();
    return (
      <Allowed action="quote.update">
        <QuoteForm key={leadId ?? clientId ?? "new"} {...(leadId ? { leadId } : {})} {...(clientId ? { clientId } : {})} />
      </Allowed>
    );
  },
});

const quoteEdit = createRoute({
  getParentRoute: () => app,
  path: "/quotes/$id/edit",
  component: function QuoteEditRoute() {
    const { id } = quoteEdit.useParams();
    return (
      <Allowed action="quote.update">
        <QuoteForm key={id} quoteId={id} />
      </Allowed>
    );
  },
});

const quotePage = createRoute({
  getParentRoute: () => app,
  path: "/quotes/$id",
  component: function QuoteRoute() {
    const { id } = quotePage.useParams();
    return (
      <Allowed action="quote.read">
        <QuotePage key={id} id={id} />
      </Allowed>
    );
  },
});

/** The agency's Proposals are its own (draft 13); every other business gets commercial cleaning bids. */
function ProposalsRoute() {
  const { company } = useSession();
  return company.trade === "agency" ? <Proposals /> : <ProposalsPage />;
}

const proposalEdit = createRoute({
  getParentRoute: () => app,
  path: "/proposals/$id",
  component: function ProposalEditRoute() {
    const { id } = proposalEdit.useParams();
    return (
      <Allowed action="quote.update">
        <ProposalBuilder key={id} id={id} />
      </Allowed>
    );
  },
});

const requestPage = createRoute({
  getParentRoute: () => app,
  path: "/requests/$id",
  component: function RequestRoute() {
    const { id } = requestPage.useParams();
    return (
      <Allowed action="lead.read">
        <RequestPage key={id} id={id} />
      </Allowed>
    );
  },
});

const requestsList = createRoute({
  getParentRoute: () => app,
  path: "/requests",
  validateSearch: newSearch,
  component: () => (
    <Allowed action="lead.read">
      <RequestsPage />
    </Allowed>
  ),
});

export const salesRoutes = [
  page("/proposals", () => (
    <Allowed action="quote.read">
      <ProposalsRoute />
    </Allowed>
  )),
  page("/proposals/new", () => (
    <Allowed action="quote.update">
      <ProposalBuilder />
    </Allowed>
  )),
  page("/quotes/settings", () => (
    <Allowed action="settings.read">
      <FeatureSettings title="Quote settings" back="/quotes" backLabel="All quotes">
        <SectionMessages section="quotes" />
        <PriceSettings />
      </FeatureSettings>
    </Allowed>
  )),
  page("/requests/settings", () => (
    <Allowed action="settings.read">
      <FeatureSettings title="Request settings" back="/requests" backLabel="All requests">
        <SectionMessages section="requests" />
        <WebsiteSettings />
      </FeatureSettings>
    </Allowed>
  )),
  page("/proposals/settings", () => (
    <Allowed action="settings.read">
      <ProposalSettingsPage />
    </Allowed>
  )),
  proposalEdit,
  quotesList,
  quoteNew,
  quotePage,
  quoteEdit,
  // The pipeline moved into Quotes (2026-10-28), its own view since 2026-11-03. Old links still land on it.
  page("/pipeline", () => <Navigate to="/quotes" search={{ view: "pipeline" }} replace />),
  requestsList,
  requestPage,
  sectionMessagePage("/quotes/settings/messages/$kind", "quotes"),
  sectionMessagePage("/requests/settings/messages/$kind", "requests"),
] as const;
